import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subscription, forkJoin, firstValueFrom } from 'rxjs';
import { SalaService, SpaceFeatureInput } from '../../services/sala.service';
import { SpaceFeaturesEditorComponent } from '../space-features-editor/space-features-editor.component';
import { SpaceExtrasEditorComponent, StagedExtra } from '../space-extras-editor/space-extras-editor.component';
import { SpaceDraftsModalComponent } from './space-drafts-modal.component';
import { AuthService } from '../../services/auth.service';
import { SubscriptionService } from '../../services/subscription.service';
import { SpaceScheduleService } from '../../services/space-schedule.service';
import { SpaceDraftError, SpaceDraftImage, SpaceDraftSummary, SpaceDraftsService } from '../../services/space-drafts.service';
import { CreateEspacioRequest, CreatePricingTierRequest, CreateScheduleRequest } from '../../models/sala.model';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { friendlyError } from '../../utils/friendly-error';
import { PlatformSettingsService } from '../../services/platform-settings.service';
import { GoogleLocationPickerComponent, SpaceMapLocation } from '../ui/google-location-picker.component';
import { CategoryAutocompleteComponent } from '../ui/category-autocomplete.component';
import { LocationAutocompleteComponent } from '../ui/location-autocomplete.component';
import { EL_SALVADOR_DEPARTMENTS, citiesForDepartment } from '../../data/el-salvador-locations';
import { formatClockTime } from '../../utils/format';
import { halfHourIncrementValidator } from '../../utils/form-validation';
import { IMAGE_UPLOAD_ACCEPT, validateSpaceImages } from '../../utils/image-upload';
import { DateTimePickerDirective } from '../../directives/date-time-picker.directive';
import { InfoTipComponent } from '../ui/info-tip.component';
import { PhotoTipsComponent } from '../ui/photo-tips.component';
import { EsDatePipe } from '../../shared/es-date.pipe';

type StepId = 'info' | 'precio' | 'ubicacion' | 'horarios';

interface StepDef {
  id: StepId;
  label: string;
  /** Campos del formulario principal que se validan en esta sección. */
  fields: string[];
}

interface ScheduleDay { closed: boolean; opening: string; closing: string; }

/** Estado del formulario que se guarda en un borrador (sin fotos). */
interface SpaceDraftData {
  form: Record<string, unknown>;
  tiers: CreatePricingTierRequest[];
  extras: StagedExtra[];
  features: SpaceFeatureInput[];
  useCustomSchedule: boolean;
  schedule: ScheduleDay[];
}

interface LoadedDraft { id: number; version: number; updatedBy: string | null; updatedAt: string; }

/** Foto en el formulario: ya guardada (image_id) o subiéndose (vista previa local). */
interface PhotoItem { key: string; image_id?: number; url: string; uploading: boolean; }

const STEPS: StepDef[] = [
  { id: 'info', label: 'Información y fotos', fields: ['title', 'description', 'categories'] },
  { id: 'precio', label: 'Capacidad y precio', fields: ['capacity', 'price_per_hour'] },
  { id: 'ubicacion', label: 'Ubicación', fields: ['country', 'department', 'city', 'address_line1', 'latitude'] },
  { id: 'horarios', label: 'Horarios y revisión', fields: [] },
];

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const defaultSchedule = (): ScheduleDay[] => DAYS.map((_, i) => ({ closed: i === 0, opening: '08:00', closing: '17:00' }));
const TITLE_MIN = 3;
const DESCRIPTION_MIN = 20;
const CAPACITY_MAX = 9999;
const PRICE_MAX = 99999.99;

/**
 * Crear un espacio: modal grande con 4 secciones (información y fotos,
 * capacidad y precio con extras, ubicación, horarios y revisión). Cada
 * sección se valida al avanzar; los errores solo aparecen en campos tocados
 * o tras intentar avanzar. El estado de cada sección se calcula con los
 * datos reales (no con "visitado"), así un borrador cargado se ve igual en
 * cualquier sección. Los borradores se guardan en el servidor y los puede
 * continuar cualquier persona del negocio con permiso, desde otro equipo.
 */
@Component({
  selector: 'app-crear-sala',
  standalone: true,
  imports: [EsDatePipe, CommonModule, ReactiveFormsModule, DateTimePickerDirective, InfoTipComponent, PhotoTipsComponent, GoogleLocationPickerComponent, CategoryAutocompleteComponent, LocationAutocompleteComponent, SpaceFeaturesEditorComponent, SpaceExtrasEditorComponent, SpaceDraftsModalComponent],
  template: `
    <div class="cs-overlay" (mousedown)="onOverlayMouseDown($event)">
      <section class="cs-modal" role="dialog" aria-modal="true" aria-labelledby="cs-title">
        <header class="cs-head">
          <span class="cs-head-icon" aria-hidden="true"><i class="bi bi-building-add"></i></span>
          <div class="cs-head-text">
            <h1 id="cs-title">{{ crearSalaForm.get('title')?.value?.trim() || 'Nuevo espacio' }}</h1>
            <small *ngIf="loadedDraft() as draft; else notDraft"><i class="bi bi-cloud-check" aria-hidden="true"></i> Borrador guardado {{ draft.updatedAt | esDate:'d MMM, h:mm a' }}{{ draft.updatedBy ? ' por ' + draft.updatedBy : '' }}{{ hasUnsavedChanges() ? ' · cambios sin guardar' : '' }}</small>
            <ng-template #notDraft><small>Completa las 4 secciones y crea tu espacio.</small></ng-template>
          </div>
          <button type="button" class="cs-btn ghost sm" (click)="openDrafts()"><i class="bi bi-journal-text" aria-hidden="true"></i> <span>Borradores</span><b class="cs-count" *ngIf="draftCount()">{{ draftCount() }}</b></button>
          <button type="button" class="cs-icon-btn" (click)="requestClose()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
        </header>

        <nav class="cs-tabs" aria-label="Secciones del espacio">
          <button type="button" *ngFor="let step of steps; let i = index" (click)="goTo(step.id)"
            [class.current]="step.id === currentStep()" [class.done]="stepState(step.id) === 'done'" [class.error]="stepState(step.id) === 'error'"
            [attr.aria-current]="step.id === currentStep() ? 'step' : null">
            <span class="cs-dot">
              <i *ngIf="stepState(step.id) === 'done'" class="bi bi-check-lg" aria-hidden="true"></i>
              <i *ngIf="stepState(step.id) === 'error'" class="bi bi-exclamation-lg" aria-hidden="true"></i>
              <span *ngIf="stepState(step.id) === 'todo'">{{ i + 1 }}</span>
            </span>
            <span class="cs-tab-text"><strong>{{ step.label }}</strong><small>{{ stepCaption(step) }}</small></span>
          </button>
        </nav>

        <form #crearForm [formGroup]="crearSalaForm" (ngSubmit)="onSubmit()" class="cs-content" novalidate>
          <aside class="cs-banner" *ngIf="draftCount() && !loadedDraft() && !hasUnsavedChanges() && currentStep() === 'info'">
            <i class="bi bi-journal-arrow-up" aria-hidden="true"></i>
            <span>Tu equipo tiene <strong>{{ draftCount() }} borrador{{ draftCount() === 1 ? '' : 'es' }}</strong> guardado{{ draftCount() === 1 ? '' : 's' }}. Puedes continuar uno en lugar de empezar de cero.</span>
            <button type="button" class="cs-btn sm" (click)="openDrafts()">Ver borradores</button>
          </aside>

          <!-- 1. Información y fotos -->
          <div class="cs-panel" [class.hidden]="currentStep() !== 'info'">
            <div class="cs-cols">
              <section class="cs-card">
                <h2 class="cs-card-title"><i class="bi bi-card-text" aria-hidden="true"></i> Datos principales</h2>
                <div class="cs-field" data-field="title">
                  <label for="cs-name">Nombre del espacio *</label>
                  <input id="cs-name" type="text" formControlName="title" class="cs-input" [class.invalid]="err('title')" placeholder="Ej. Sala de reuniones Ártico" maxlength="100" (blur)="touch('title')" />
                  <small class="cs-err" *ngIf="err('title') as message">{{ message }}</small>
                </div>
                <div class="cs-field" data-field="description">
                  <label for="cs-desc">Descripción *</label>
                  <textarea id="cs-desc" formControlName="description" class="cs-input" [class.invalid]="err('description')" rows="4" maxlength="2000" placeholder="Qué tiene el espacio, para qué es ideal y detalles útiles (acceso, ambiente, vista...)." (blur)="touch('description')"></textarea>
                  <small class="cs-help" *ngIf="!err('description')">Mínimo {{ descriptionMin }} caracteres.</small>
                  <small class="cs-err" *ngIf="err('description') as message">{{ message }}</small>
                </div>
                <div class="cs-field" data-field="categories">
                  <label>Categorías * <app-info-tip text="Elige hasta 8 tipos de uso. La primera es la principal: con ella aparece el espacio en las búsquedas y en su ficha."></app-info-tip></label>
                  <app-category-autocomplete [options]="categories" [selected]="crearSalaForm.get('categories')?.value || []" [max]="8" [invalid]="!!err('categories')" (selectedChange)="setCategories($event)"></app-category-autocomplete>
                  <small class="cs-err" *ngIf="err('categories') as message">{{ message }}</small>
                </div>
              </section>

              <section class="cs-card">
                <h2 class="cs-card-title"><i class="bi bi-images" aria-hidden="true"></i> Fotos <app-photo-tips kind="space"></app-photo-tips></h2>
                <p class="cs-help">Se guardan al instante en el borrador; la primera será la portada. Quitar una foto la elimina definitivamente.</p>
                <div class="cs-photos">
                  <div class="cs-photo" *ngFor="let image of selectedImages(); let i = index; trackBy: trackPhoto" [class.uploading]="image.uploading">
                    <img [src]="image.url" [alt]="'Foto ' + (i + 1)" />
                    <span class="cs-photo-badge" *ngIf="i === 0 && !image.uploading">Portada</span>
                    <span class="cs-photo-progress" *ngIf="image.uploading"><i class="bi bi-arrow-repeat" aria-hidden="true"></i> Subiendo…</span>
                    <button type="button" class="cs-photo-remove" *ngIf="!image.uploading" (click)="removeSelectedImage(image)" [disabled]="removingPhoto() === image.key" aria-label="Eliminar foto"><i class="bi bi-trash3"></i></button>
                  </div>
                  <button type="button" class="cs-photo-add" (click)="triggerFileInput()" [disabled]="uploadingImages()">
                    <i class="bi" [ngClass]="uploadingImages() ? 'bi-arrow-repeat' : 'bi-camera'" aria-hidden="true"></i><span>{{ uploadingImages() ? 'Subiendo…' : 'Agregar' }}</span>
                  </button>
                </div>
                <input #fileInput type="file" [attr.accept]="imageAccept" multiple hidden (change)="onFilesSelected($event)" />
              </section>
            </div>
            <ng-container *ngFor="let gen of [editorsGen()]; trackBy: trackGen">
              <app-space-features-editor [staged]="true" [initialStaged]="draftFeatures" (stagedChange)="onFeaturesChange($event)"></app-space-features-editor>
            </ng-container>
          </div>

          <!-- 2. Capacidad, precio y extras -->
          <div class="cs-panel" [class.hidden]="currentStep() !== 'precio'">
            <div class="cs-cols">
              <section class="cs-card">
                <h2 class="cs-card-title"><i class="bi bi-people" aria-hidden="true"></i> Capacidad y precio</h2>
                <div class="cs-grid-2">
                  <div class="cs-field" data-field="capacity">
                    <label for="cs-capacity">Capacidad (personas) *</label>
                    <input id="cs-capacity" type="number" inputmode="numeric" formControlName="capacity" class="cs-input" [class.invalid]="err('capacity')" min="1" [max]="capacityMax" step="1" placeholder="Ej. 10" (blur)="touch('capacity')" [attr.data-max-message]="'La capacidad máxima es ' + capacityMax" />
                    <small class="cs-err" *ngIf="err('capacity') as message">{{ message }}</small>
                  </div>
                  <div class="cs-field" data-field="price_per_hour">
                    <label for="cs-price">Precio por hora (USD) * <app-info-tip text="Lo que paga el cliente por cada hora. TuSala descuenta su comisión de lo facturado; abajo ves cuánto recibirías."></app-info-tip></label>
                    <div class="cs-money" [class.invalid]="err('price_per_hour')">
                      <span aria-hidden="true">$</span>
                      <input id="cs-price" type="number" inputmode="decimal" formControlName="price_per_hour" class="cs-input" min="0" [max]="priceMax" step="0.01" placeholder="0.00" (blur)="touch('price_per_hour')" [attr.data-max-message]="'El precio máximo es $' + priceMax" />
                    </div>
                    <small class="cs-err" *ngIf="err('price_per_hour') as message">{{ message }}</small>
                  </div>
                </div>
                <p class="cs-note" *ngIf="netPricePerHour() > 0; else commissionNote"><i class="bi bi-wallet2" aria-hidden="true"></i> Recibirías <strong>{{ netPricePerHour() | currency:'USD' }}</strong> por hora (comisión de {{ commissionRate() }}% descontada).</p>
                <ng-template #commissionNote><p class="cs-note"><i class="bi bi-info-circle" aria-hidden="true"></i> TuSala cobra una comisión de {{ commissionRate() }}% sobre lo facturado.</p></ng-template>
              </section>

              <section class="cs-card">
                <h2 class="cs-card-title"><i class="bi bi-tags" aria-hidden="true"></i> Precio por horas <span class="cs-optional">Opcional</span> <app-info-tip text="Cobra un total fijo cuando la reserva dura cierto rango de horas. Ej.: de 2 a 4 horas por $15 en total, en lugar de multiplicar el precio por hora."></app-info-tip></h2>
                <p class="cs-help">Rangos con precio total fijo. Sin rangos, se cobra el precio por hora.</p>
                <ul class="cs-tiers" *ngIf="stagedTiers().length">
                  <li *ngFor="let tier of stagedTiers(); let i = index">
                    <span>{{ tier.min_hours }}–{{ tier.max_hours }} h</span>
                    <strong>{{ tier.total_price | currency:'USD' }}</strong>
                    <button type="button" class="cs-icon-btn xs" (click)="removeStagedTier(i)" aria-label="Quitar rango"><i class="bi bi-x-lg"></i></button>
                  </li>
                </ul>
                <div class="cs-tier-form" [formGroup]="tierDraftForm">
                  <input id="cs-tier-min" type="number" formControlName="min_hours" class="cs-input" min="1" step="0.5" placeholder="Desde (h)" aria-label="Desde (horas)" />
                  <input id="cs-tier-max" type="number" formControlName="max_hours" class="cs-input" min="1" step="0.5" placeholder="Hasta (h)" aria-label="Hasta (horas)" />
                  <input id="cs-tier-price" type="number" formControlName="total_price" class="cs-input" min="0" step="0.01" placeholder="Total $" aria-label="Precio total (USD)" />
                  <button type="button" class="cs-btn sm" (click)="addStagedTier()" aria-label="Agregar rango"><i class="bi bi-plus-lg" aria-hidden="true"></i></button>
                </div>
                <small class="cs-err" *ngIf="tierError()">{{ tierError() }}</small>
              </section>
            </div>
            <section class="cs-card">
              <ng-container *ngFor="let gen of [editorsGen()]; trackBy: trackGen">
                <app-space-extras-editor [staged]="true" [initialStaged]="draftExtras" (stagedChange)="onExtrasChange($event)"></app-space-extras-editor>
              </ng-container>
            </section>
          </div>

          <!-- 3. Ubicación -->
          <div class="cs-panel" [class.hidden]="currentStep() !== 'ubicacion'">
            <section class="cs-card">
              <h2 class="cs-card-title"><i class="bi bi-geo-alt" aria-hidden="true"></i> Dirección</h2>
              <div class="cs-grid-4">
                <div class="cs-field" data-field="country">
                  <label for="cs-country">País *</label>
                  <input id="cs-country" type="text" formControlName="country" class="cs-input" data-no-limit-hint [class.invalid]="err('country')" placeholder="País" maxlength="100" (blur)="touch('country')" />
                  <small class="cs-err" *ngIf="err('country') as message">{{ message }}</small>
                </div>
                <div class="cs-field" data-field="department">
                  <label>Departamento *</label>
                  <app-location-autocomplete [options]="departments" [value]="crearSalaForm.get('department')?.value || ''" [allowCustom]="false" placeholder="Busca un departamento" [invalid]="!!err('department')" (valueChange)="setDepartment($event)"></app-location-autocomplete>
                  <small class="cs-err" *ngIf="err('department') as message">{{ message }}</small>
                </div>
                <div class="cs-field" data-field="city">
                  <label>Ciudad *</label>
                  <app-location-autocomplete [options]="citiesForSelectedDepartment()" [value]="crearSalaForm.get('city')?.value || ''" [allowCustom]="true" [disabled]="!crearSalaForm.get('department')?.value" [placeholder]="crearSalaForm.get('department')?.value ? 'Busca o escribe' : 'Elige el departamento'" [invalid]="!!err('city')" (valueChange)="setCity($event)"></app-location-autocomplete>
                  <small class="cs-err" *ngIf="err('city') as message">{{ message }}</small>
                </div>
                <div class="cs-field" data-field="address_line1">
                  <label for="cs-address">Dirección *</label>
                  <input id="cs-address" type="text" formControlName="address_line1" class="cs-input" data-no-limit-hint [class.invalid]="err('address_line1')" placeholder="Calle, número, edificio…" maxlength="255" (blur)="touch('address_line1')" />
                  <small class="cs-err" *ngIf="err('address_line1') as message">{{ message }}</small>
                </div>
              </div>
              <div class="cs-field" data-field="latitude">
                <app-google-location-picker [frameless]="true" [address]="fullAddress" [latitude]="crearSalaForm.get('latitude')?.value" [longitude]="crearSalaForm.get('longitude')?.value" (locationChange)="setMapLocation($event)" />
                <small class="cs-err" *ngIf="err('latitude') as message">{{ message }}</small>
              </div>
            </section>
          </div>

          <!-- 4. Horarios y revisión -->
          <div class="cs-panel" [class.hidden]="currentStep() !== 'horarios'">
            <div class="cs-cols wide-left">
              <section class="cs-card">
                <h2 class="cs-card-title"><i class="bi bi-clock" aria-hidden="true"></i> Horario de atención <app-info-tip text="Define cuándo se puede reservar. Las horas van en punto o y media (ej. 8:00 u 8:30). Sin restricción, se puede reservar cualquier día y hora."></app-info-tip></h2>
                <div class="cs-choice" role="radiogroup" aria-label="Horario del espacio">
                  <button type="button" role="radio" [attr.aria-checked]="!useCustomSchedule()" [class.on]="!useCustomSchedule()" (click)="setCustomSchedule(false)">
                    <i class="bi bi-infinity" aria-hidden="true"></i><span><strong>Sin restricción</strong><small>Todos los días, cualquier hora</small></span>
                  </button>
                  <button type="button" role="radio" [attr.aria-checked]="useCustomSchedule()" [class.on]="useCustomSchedule()" (click)="setCustomSchedule(true)">
                    <i class="bi bi-calendar-week" aria-hidden="true"></i><span><strong>Horario específico</strong><small>Días y horas de atención</small></span>
                  </button>
                </div>
                <ng-container *ngIf="useCustomSchedule()">
                  <div class="cs-presets">
                    <button type="button" class="cs-chip" (click)="applyPreset('weekdays')">L–V · 8:00–17:00</button>
                    <button type="button" class="cs-chip" (click)="applyPreset('all')">Todos los días · 8:00–17:00</button>
                    <button type="button" class="cs-chip" (click)="copyFirstOpenDay()">Copiar el primer día abierto</button>
                  </div>
                  <div class="cs-days">
                    <div class="cs-day" *ngFor="let day of schedule(); let i = index; trackBy: trackDay" [class.closed]="day.closed" [class.invalid]="scheduleAttempted() && dayError(i)">
                      <label class="cs-switch">
                        <input type="checkbox" [checked]="!day.closed" (change)="setDayOpen(i, $any($event.target).checked)" />
                        <span>{{ dayNames[i] }}</span>
                      </label>
                      <div class="cs-day-times" *ngIf="!day.closed">
                        <input type="text" appDatePicker="time" class="cs-input" [value]="day.opening" (pickerValueChange)="setDayTime(i, 'opening', $event)" placeholder="Apertura" [attr.aria-label]="'Apertura ' + dayNames[i]" />
                        <span aria-hidden="true">a</span>
                        <input type="text" appDatePicker="time" class="cs-input" [value]="day.closing" [afterTime]="day.opening" afterTimeMessage="El cierre debe ser después de la apertura" (pickerValueChange)="setDayTime(i, 'closing', $event)" placeholder="Cierre" [attr.aria-label]="'Cierre ' + dayNames[i]" />
                      </div>
                      <span class="cs-day-closed" *ngIf="day.closed">Cerrado</span>
                      <small class="cs-err" *ngIf="scheduleAttempted() && dayError(i) as message">{{ message }}</small>
                    </div>
                  </div>
                  <small class="cs-err" *ngIf="scheduleAttempted() && allClosed()">Deja al menos un día abierto o elige «Sin restricción».</small>
                </ng-container>
              </section>

              <section class="cs-card cs-summary">
                <h2 class="cs-card-title"><i class="bi bi-clipboard-check" aria-hidden="true"></i> Resumen</h2>
                <ul class="cs-check">
                  <li *ngFor="let step of steps.slice(0, 3)" [class.ok]="stepValid(step.id)">
                    <i class="bi" [ngClass]="stepValid(step.id) ? 'bi-check-circle-fill' : 'bi-circle'" aria-hidden="true"></i>
                    <span>{{ step.label }}</span>
                    <button type="button" class="cs-link" (click)="goTo(step.id)">{{ stepValid(step.id) ? 'Ver' : 'Completar' }}</button>
                  </li>
                </ul>
                <dl class="cs-facts">
                  <div><dt>Capacidad</dt><dd>{{ crearSalaForm.get('capacity')?.value || '—' }} personas</dd></div>
                  <div><dt>Precio</dt><dd>{{ (crearSalaForm.get('price_per_hour')?.value || 0) | currency:'USD' }} / hora</dd></div>
                  <div><dt>Fotos</dt><dd>{{ selectedImages().length || 'Ninguna' }}</dd></div>
                  <div><dt>Incluye</dt><dd>{{ stagedFeatures().length }} característica(s)</dd></div>
                  <div><dt>Extras</dt><dd>{{ stagedExtras().length }}{{ limitedExtras() ? ' (' + limitedExtras() + ' con máximo)' : '' }}</dd></div>
                  <div><dt>Horario</dt><dd>{{ scheduleSummary() }}</dd></div>
                </dl>
                <ul class="cs-warnings" *ngIf="reviewWarnings().length">
                  <li *ngFor="let warning of reviewWarnings()"><i class="bi bi-lightbulb" aria-hidden="true"></i> {{ warning }}</li>
                </ul>
              </section>
            </div>
          </div>
        </form>

        <footer class="cs-foot">
          <button type="button" class="cs-btn ghost" (click)="saveDraft()" [disabled]="isCreating() || savingDraft()">
            <i class="bi" [ngClass]="savingDraft() ? 'bi-arrow-repeat' : 'bi-cloud-arrow-up'" aria-hidden="true"></i> <span>{{ savingDraft() ? 'Guardando…' : 'Guardar borrador' }}</span>
          </button>
          <span class="cs-foot-msg" *ngIf="stepMessage() && !stepValid(currentStep())" role="alert"><i class="bi bi-exclamation-circle" aria-hidden="true"></i> {{ stepMessage() }}</span>
          <span class="cs-foot-spacer"></span>
          <button type="button" class="cs-btn" (click)="back()" *ngIf="stepIndex() > 0" [disabled]="isCreating()"><i class="bi bi-arrow-left" aria-hidden="true"></i> Atrás</button>
          <button type="button" class="cs-btn primary" *ngIf="currentStep() !== 'horarios'" (click)="next()">Siguiente <i class="bi bi-arrow-right" aria-hidden="true"></i></button>
          <button type="button" class="cs-btn primary" *ngIf="currentStep() === 'horarios'" (click)="createEspacio()" [disabled]="isCreating() || uploadingImages()">
            <i class="bi bi-check2-circle" aria-hidden="true"></i> {{ uploadingImages() ? 'Subiendo fotos…' : (isCreating() ? 'Creando…' : 'Crear espacio') }}
          </button>
        </footer>

        <!-- Salir con cambios -->
        <div class="cs-layer" *ngIf="exitPromptOpen()">
          <div class="cs-layer-card" role="alertdialog" aria-modal="true" aria-labelledby="cs-exit-title">
            <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
            <h2 id="cs-exit-title">¿Salir sin terminar?</h2>
            <p>Tienes cambios sin guardar. Guarda el borrador y tú o alguien de tu equipo podrá continuarlo después.{{ loadedDraft() ? ' Si sales sin guardar, el borrador conserva lo último guardado y sus fotos.' : '' }}</p>
            <div class="cs-layer-actions">
              <button type="button" class="cs-btn primary" (click)="saveDraftAndClose()" [disabled]="savingDraft()"><i class="bi bi-cloud-arrow-up" aria-hidden="true"></i> Guardar borrador y salir</button>
              <button type="button" class="cs-btn danger" (click)="closeNow()">Salir sin guardar</button>
              <button type="button" class="cs-btn ghost" (click)="exitPromptOpen.set(false)">Seguir editando</button>
            </div>
          </div>
        </div>

        <!-- Conflicto: otra persona guardó el mismo borrador -->
        <div class="cs-layer" *ngIf="conflict() as info">
          <div class="cs-layer-card" role="alertdialog" aria-modal="true" aria-labelledby="cs-conflict-title">
            <i class="bi bi-people" aria-hidden="true"></i>
            <h2 id="cs-conflict-title">El borrador cambió</h2>
            <p>{{ info }} ¿Qué quieres hacer con tus cambios?</p>
            <div class="cs-layer-actions">
              <button type="button" class="cs-btn primary" (click)="saveAsNewDraft()"><i class="bi bi-files" aria-hidden="true"></i> Guardar mis cambios como borrador nuevo</button>
              <button type="button" class="cs-btn" (click)="reloadLoadedDraft()"><i class="bi bi-arrow-clockwise" aria-hidden="true"></i> Cargar su versión (descarta mis cambios)</button>
              <button type="button" class="cs-btn ghost" (click)="conflict.set(null)">Cancelar</button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <app-space-drafts-modal *ngIf="draftsOpen() && businessId" [businessId]="businessId" [currentDraftId]="loadedDraft()?.id ?? null"
      (select)="onDraftSelected($event)" (closed)="onDraftsClosed()" (countChange)="draftCount.set($event)"></app-space-drafts-modal>
  `,
  styles: [`
    :host { display: contents; }
    .cs-overlay { position: fixed; inset: 0; z-index: 2500; display: grid; place-items: center; padding: clamp(.5rem, 2vw, 1.5rem); background: var(--ts-bg-navy-950-a74); backdrop-filter: blur(4px); }
    .cs-modal { position: relative; width: min(1160px, 100%); height: min(900px, calc(100dvh - 2rem)); display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: auto auto minmax(0, 1fr) auto; overflow: hidden; border-radius: 18px; border: 1px solid var(--ts-border-strong); background: var(--ts-bg); box-shadow: 0 30px 80px var(--ts-shadow-gray-1000-a45); color: var(--ts-text); }
    .cs-head { display: flex; align-items: center; gap: .7rem; padding: .75rem 1.25rem; background: var(--ts-bg-elevated); }
    .cs-head-icon { width: 38px; height: 38px; flex: none; display: grid; place-items: center; border-radius: 12px; color: var(--ts-text-blue-250-3); background: var(--ts-bg-blue-400-a14); font-size: 1.15rem; }
    .cs-head-text { flex: 1; min-width: 0; }
    .cs-head h1 { margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 1.02rem; letter-spacing: -.02em; }
    .cs-head small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ts-text-muted); font-size: .74rem; }
    .cs-head small i { color: var(--ts-text-green-350); }
    .cs-count { min-width: 18px; height: 18px; display: inline-grid; place-items: center; padding: 0 .3rem; border-radius: 999px; color: var(--ts-text-on-accent); background: var(--ts-primary, #2f7cff); font-size: .66rem; }
    .cs-tabs { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); padding: 0 1.25rem; border-bottom: 1px solid var(--ts-border); background: var(--ts-bg-elevated); }
    .cs-tabs button { position: relative; display: flex; align-items: center; gap: .55rem; min-width: 0; padding: .6rem .5rem .7rem; border: 0; border-radius: 0; color: var(--ts-text-soft); background: transparent; font: inherit; text-align: left; cursor: pointer; transition: color .15s ease; }
    .cs-tabs button::after { content: ''; position: absolute; left: .5rem; right: .5rem; bottom: -1px; height: 3px; border-radius: 3px 3px 0 0; background: transparent; transition: background .15s ease; }
    .cs-tabs button.done::after { background: var(--ts-bg-teal-550-a55); }
    .cs-tabs button.error::after { background: var(--ts-bg-red-350-a70); }
    .cs-tabs button.current::after { background: var(--ts-primary, #2f7cff); }
    .cs-tabs button:hover { color: var(--ts-text); }
    .cs-tabs button.current { color: var(--ts-text); }
    .cs-dot { width: 24px; height: 24px; flex: none; display: grid; place-items: center; border-radius: 50%; border: 1.5px solid var(--ts-border-strong); font-size: .7rem; font-weight: 800; }
    .current .cs-dot { border-color: var(--ts-primary, #2f7cff); color: var(--ts-text-on-accent); background: var(--ts-primary, #2f7cff); }
    .done .cs-dot { border-color: var(--ts-border-teal-550); color: var(--ts-text-on-accent); background: var(--ts-bg-teal-550); }
    .error .cs-dot { border-color: var(--ts-border-red-350); color: var(--ts-text-on-accent); background: var(--ts-bg-red-350); }
    .cs-tab-text { display: grid; min-width: 0; }
    .cs-tab-text strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: .8rem; }
    .cs-tab-text small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ts-text-muted); font-size: .68rem; }
    .error .cs-tab-text small { color: var(--ts-danger-text, #ff8a8a); }
    .done .cs-tab-text small { color: var(--ts-text-green-350); }
    .cs-content { min-height: 0; min-width: 0; overflow-x: hidden; overflow-y: auto; padding: 1.1rem clamp(.9rem, 2.5vw, 1.6rem) 1.4rem; }
    .cs-panel { display: grid; grid-template-columns: minmax(0, 1fr); gap: 1rem; min-width: 0; animation: csIn .18s ease; }
    .cs-panel.hidden { display: none; }
    @keyframes csIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
    .cs-cols { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr); gap: 1rem; align-items: stretch; }
    .cs-cols.wide-left { grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
    .cs-card { min-width: 0; padding: .95rem 1.05rem; border: 1px solid var(--ts-border); border-radius: 12px; background: var(--ts-bg-elevated); }
    .cs-card-title { display: flex; align-items: center; gap: .5rem; margin: 0 0 .85rem; font-size: .9rem; letter-spacing: -.01em; }
    .cs-card-title i { width: 26px; height: 26px; display: grid; place-items: center; border-radius: 8px; color: var(--ts-text-blue-250-3); background: var(--ts-bg-blue-400-a12); font-size: .82rem; }
    .cs-optional { padding: .05rem .45rem; border-radius: 999px; border: 1px solid var(--ts-border); color: var(--ts-text-muted); font-size: .64rem; font-weight: 700; }
    .cs-field { display: grid; align-content: start; gap: .3rem; min-width: 0; margin-bottom: .85rem; }
    .cs-field:last-child { margin-bottom: 0; }
    .cs-overlay .cs-modal .cs-field > label { display: flex; align-items: center; gap: .25rem; height: 22px; min-height: 22px; margin: 0; padding: 0; line-height: 1.2; white-space: nowrap; color: var(--ts-text-soft); font-size: .78rem; font-weight: 750; }
    .cs-field > label small { font-weight: 500; color: var(--ts-text-muted); }
    .cs-input { width: 100%; box-sizing: border-box; height: 40px; min-height: 40px; padding: .45rem .75rem; border-radius: 10px; border: 1px solid var(--ts-border-strong); color: var(--ts-text); background: var(--ts-input-bg, var(--ts-surface-2)); font: inherit; font-size: .88rem; }
    textarea.cs-input { height: auto; min-height: 96px; resize: vertical; line-height: 1.5; }
    .cs-input::placeholder { color: var(--ts-text-muted); }
    .cs-input:focus { outline: none; border-color: var(--ts-primary); box-shadow: none; }
    .cs-input.invalid, .cs-money.invalid { border-color: var(--ts-danger, #f05259) !important; }
    .cs-money { display: flex; align-items: center; border: 1px solid var(--ts-border-strong); border-radius: 10px; background: var(--ts-input-bg, var(--ts-surface-2)); }
    .cs-money span { padding-left: .75rem; color: var(--ts-text-muted); font-weight: 700; }
    .cs-money .cs-input { height: 38px; min-height: 38px; border: 0; background: transparent; box-shadow: none !important; }
    .cs-money:focus-within { border-color: var(--ts-primary); box-shadow: none; }
    .cs-err { color: var(--ts-danger-text, #ff8a8a); font-size: .74rem; font-weight: 650; }
    .cs-help { margin: 0 0 .7rem; color: var(--ts-text-muted); font-size: .76rem; line-height: 1.45; }
    .cs-field .cs-help { margin: 0; }
    .cs-note { display: flex; align-items: center; gap: .45rem; margin: .8rem 0 0; padding: .55rem .7rem; border-radius: 10px; color: var(--ts-text-soft); background: var(--ts-bg-blue-400-a8); font-size: .78rem; }
    .cs-note i { color: var(--ts-text-blue-250-3); }
    .cs-grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 .9rem; }
    .cs-grid-2 .cs-field { margin-bottom: 0; }
    .cs-grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1.6fr; gap: 0 .9rem; }
    .cs-photos { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .5rem; }
    .cs-photo { position: relative; aspect-ratio: 4 / 3; overflow: hidden; border-radius: 10px; background: var(--ts-surface); }
    .cs-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .cs-photo-badge { position: absolute; left: .3rem; bottom: .3rem; padding: .08rem .4rem; border-radius: 999px; color: var(--ts-text-navy-900-2); background: var(--ts-bg-gray-0-a92); font-size: .62rem; font-weight: 800; }
    .cs-photo-remove { position: absolute; top: .3rem; right: .3rem; width: 24px; height: 24px; display: grid; place-items: center; border: 0; border-radius: 50%; color: var(--ts-text-gray-0); background: var(--ts-bg-navy-950-a70); cursor: pointer; font-size: .64rem; }
    .cs-photo-remove:hover { background: var(--ts-bg-red-350); }
    .cs-photo.uploading img { opacity: .45; filter: grayscale(.4); }
    .cs-photo-progress { position: absolute; inset: auto .3rem .3rem; display: flex; align-items: center; justify-content: center; gap: .3rem; padding: .15rem .4rem; border-radius: 999px; color: var(--ts-text-gray-0); background: var(--ts-bg-navy-950-a72); font-size: .66rem; font-weight: 750; }
    .cs-photo-progress i, .cs-photo-add .bi-arrow-repeat { animation: csSpin 1s linear infinite; }
    .cs-photo-add { aspect-ratio: 4 / 3; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .2rem; border: 1.5px dashed var(--ts-border-strong); border-radius: 10px; color: var(--ts-text-blue-250-3); background: var(--ts-bg-blue-400-a5); font: inherit; font-size: .78rem; font-weight: 750; cursor: pointer; }
    .cs-photo-add i { font-size: 1.15rem; }
    .cs-photo-add:hover:not(:disabled) { border-color: var(--ts-primary); background: var(--ts-bg-blue-400-a10); }
    .cs-tiers { list-style: none; margin: 0 0 .6rem; padding: 0; display: grid; gap: .35rem; }
    .cs-tiers li { display: grid; grid-template-columns: minmax(0, 1fr) auto auto; align-items: center; gap: .6rem; padding: .35rem .4rem .35rem .7rem; border: 1px solid var(--ts-border); border-radius: 9px; background: var(--ts-surface); font-size: .84rem; }
    .cs-tiers strong { color: var(--ts-text-green-350); font-variant-numeric: tabular-nums; }
    .cs-tier-form { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)) auto; gap: .45rem; }
    .cs-tier-form .cs-btn { height: 40px; min-height: 40px; width: 40px; padding: 0; }
    .cs-choice { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .5rem; margin-bottom: .8rem; }
    .cs-choice button { display: flex; align-items: center; gap: .6rem; padding: .65rem .75rem; border: 1px solid var(--ts-border-strong); border-radius: 11px; color: var(--ts-text-soft); background: var(--ts-surface); font: inherit; text-align: left; cursor: pointer; }
    .cs-choice button i { font-size: 1.1rem; color: var(--ts-text-blue-250-3); }
    .cs-choice button span { display: grid; }
    .cs-choice button strong { color: var(--ts-text); font-size: .85rem; }
    .cs-choice button small { color: var(--ts-text-muted); font-size: .72rem; }
    .cs-choice button.on { border-color: var(--ts-primary); background: var(--ts-bg-blue-400-a12); box-shadow: inset 0 0 0 1px var(--ts-primary); }
    .cs-presets { display: flex; flex-wrap: wrap; gap: .35rem; margin-bottom: .7rem; }
    .cs-chip { padding: .28rem .6rem; border: 1px solid var(--ts-border-strong); border-radius: 999px; color: var(--ts-text-soft); background: var(--ts-surface); font: inherit; font-size: .74rem; font-weight: 650; cursor: pointer; }
    .cs-chip:hover { border-color: var(--ts-primary); color: var(--ts-text); }
    .cs-days { display: grid; gap: .35rem; }
    .cs-day { display: flex; flex-wrap: wrap; align-items: center; gap: .4rem .8rem; min-height: 46px; padding: .3rem .6rem; border: 1px solid var(--ts-border); border-radius: 10px; background: var(--ts-surface); }
    .cs-day.closed { opacity: .7; }
    .cs-day.invalid { border-color: var(--ts-danger, #f05259); }
    .cs-day .cs-err { flex-basis: 100%; }
    .cs-switch { display: inline-flex; align-items: center; gap: .5rem; min-width: 110px; cursor: pointer; font-size: .84rem; font-weight: 700; }
    .cs-switch input { width: 16px; height: 16px; accent-color: var(--ts-primary); }
    .cs-day-times { display: flex; align-items: center; gap: .4rem; margin-left: auto; color: var(--ts-text-muted); font-size: .8rem; }
    .cs-overlay .cs-modal .cs-day-times input.cs-input, .cs-overlay .cs-modal .cs-day-times ::ng-deep input { width: 116px !important; height: 36px !important; min-height: 36px !important; padding: .2rem .6rem !important; border-radius: 10px !important; font-size: .84rem !important; box-sizing: border-box; }
    .cs-day-closed { margin-left: auto; color: var(--ts-text-muted); font-size: .78rem; }
    .cs-summary { position: sticky; top: 0; }
    .cs-check { list-style: none; margin: 0 0 .8rem; padding: 0; display: grid; gap: .35rem; }
    .cs-check li { display: flex; align-items: center; gap: .5rem; font-size: .84rem; color: var(--ts-text-soft); }
    .cs-check li i { color: var(--ts-text-muted); }
    .cs-check li.ok i { color: var(--ts-text-teal-550); }
    .cs-check li span { flex: 1; }
    .cs-facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .5rem .8rem; margin: 0; padding: .7rem 0 0; border-top: 1px solid var(--ts-border); }
    .cs-facts div { min-width: 0; }
    .cs-facts dt { color: var(--ts-text-muted); font-size: .68rem; font-weight: 750; text-transform: uppercase; letter-spacing: .04em; }
    .cs-facts dd { margin: .1rem 0 0; overflow-wrap: anywhere; color: var(--ts-text); font-size: .82rem; }
    .cs-warnings { list-style: none; margin: .8rem 0 0; padding: 0; display: grid; gap: .35rem; }
    .cs-warnings li { display: flex; gap: .45rem; padding: .5rem .65rem; border-radius: 9px; color: var(--ts-text-orange-200); background: var(--ts-bg-orange-350-a8); border: 1px solid var(--ts-border-orange-350-a25); font-size: .76rem; line-height: 1.4; }
    .cs-link { padding: 0; border: 0; background: none; color: var(--ts-text-blue-250-3); font: inherit; font-size: .78rem; font-weight: 750; cursor: pointer; }
    .cs-banner { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem .8rem; margin-bottom: 1rem; padding: .6rem .8rem; border: 1px solid var(--ts-border-blue-250-a40-2); border-radius: 12px; background: var(--ts-bg-blue-400-a8); font-size: .82rem; color: var(--ts-text-soft); }
    .cs-banner > i { color: var(--ts-text-blue-250-3); font-size: 1.1rem; }
    .cs-banner span { flex: 1; min-width: 200px; }
    .cs-foot { display: flex; align-items: center; gap: .5rem; padding: .7rem 1.25rem; border-top: 1px solid var(--ts-border); background: var(--ts-bg-elevated); }
    .cs-foot-spacer { flex: 1; }
    .cs-foot-msg { display: inline-flex; align-items: center; gap: .35rem; min-width: 0; color: var(--ts-danger-text, #ff8a8a); font-size: .8rem; font-weight: 650; }
    .cs-btn { display: inline-flex; align-items: center; justify-content: center; gap: .45rem; height: 38px; min-height: 38px; padding: 0 .95rem; border-radius: 10px; border: 1px solid var(--ts-border-strong); color: var(--ts-text); background: var(--ts-surface); font: inherit; font-size: .83rem; font-weight: 700; white-space: nowrap; cursor: pointer; }
    .cs-foot .cs-btn.primary, .cs-foot .cs-btn:not(.ghost) { min-width: 118px; }
    .cs-btn.sm { height: 32px; min-height: 32px; padding: 0 .7rem; font-size: .78rem; }
    .cs-head .cs-btn.sm { height: 38px; min-height: 38px; }
    .cs-btn.primary { border-color: transparent; color: var(--ts-text-on-accent); background: linear-gradient(135deg, var(--ts-bg-blue-500), var(--ts-bg-sky-450)); box-shadow: 0 6px 14px var(--ts-shadow-blue-500-a28-2); }
    .cs-btn.ghost { background: transparent; }
    .cs-btn.danger { border-color: var(--ts-border-red-350-a50); color: var(--ts-text-red-200); background: transparent; }
    .cs-btn:disabled { opacity: .5; cursor: not-allowed; }
    .cs-btn .bi-arrow-repeat { animation: csSpin 1s linear infinite; }
    @keyframes csSpin { to { transform: rotate(360deg); } }
    .cs-icon-btn { width: 38px; height: 38px; min-height: 0; padding: 0; flex: none; display: grid; place-items: center; border-radius: 10px; border: 1px solid var(--ts-border); color: var(--ts-text-soft); background: var(--ts-surface); cursor: pointer; }
    .cs-icon-btn.xs { width: 26px; height: 26px; border-color: transparent; background: transparent; font-size: .7rem; }
    .cs-icon-btn:hover { color: var(--ts-text); border-color: var(--ts-border-strong); }
    .cs-layer { position: absolute; inset: 0; z-index: 5; display: grid; place-items: center; padding: 1rem; background: var(--ts-bg-navy-950-a70-2); }
    .cs-layer-card { width: min(470px, 100%); padding: 1.4rem; border-radius: 16px; border: 1px solid var(--ts-border-strong); background: var(--ts-bg-elevated); text-align: center; box-shadow: 0 20px 60px var(--ts-shadow-gray-1000-a40); }
    .cs-layer-card > i { color: var(--ts-text-orange-250); font-size: 1.8rem; }
    .cs-layer-card h2 { margin: .4rem 0; font-size: 1.12rem; }
    .cs-layer-card p { margin: 0 0 1.1rem; color: var(--ts-text-soft); font-size: .85rem; line-height: 1.5; }
    .cs-layer-actions { display: grid; gap: .5rem; }
    .cs-overlay .cs-modal ::ng-deep .cat-ac-control,
    .cs-overlay .cs-modal ::ng-deep .loc-ac-control { height: 40px !important; min-height: 40px !important; border-radius: 10px !important; box-shadow: none !important; }
    .cs-overlay .cs-modal ::ng-deep .cat-ac-control:focus-within,
    .cs-overlay .cs-modal ::ng-deep .loc-ac-control:focus-within { border-color: var(--ts-primary) !important; box-shadow: none !important; }
    .cs-overlay .cs-modal ::ng-deep .cat-ac-control input,
    .cs-overlay .cs-modal ::ng-deep .loc-ac-control input { height: 38px !important; min-height: 38px !important; padding: 0 !important; border: 0 !important; outline: none !important; box-shadow: none !important; background: transparent !important; font-size: .86rem !important; }
    .cs-overlay .cs-modal ::ng-deep app-google-location-picker .search input { height: 40px !important; min-height: 40px !important; padding: 0 .75rem 0 2.15rem !important; font-size: .86rem !important; box-shadow: none !important; }
    .cs-overlay .cs-modal input.cs-input:not(textarea) { height: 40px !important; min-height: 40px !important; margin: 0 !important; }
    .cs-overlay .cs-modal .cs-money input.cs-input { height: 38px !important; min-height: 38px !important; border: 0 !important; outline: none !important; box-shadow: none !important; background: transparent !important; padding-left: .45rem !important; }
    .cs-modal ::ng-deep .cat-chip { height: 28px !important; min-height: 28px !important; padding: 0 .3rem 0 .6rem !important; font-size: .74rem !important; }
    .cs-modal ::ng-deep .cat-chip button { width: 20px !important; height: 20px !important; min-height: 0 !important; padding: 0 !important; }
    .cs-modal ::ng-deep .features-section.compact { padding: .95rem 1.05rem; border-radius: 12px; border-color: var(--ts-border); background: var(--ts-bg-elevated); }
    @media (max-width: 980px) {
      .cs-cols, .cs-cols.wide-left { grid-template-columns: 1fr; }
      .cs-grid-4 { grid-template-columns: 1fr 1fr; }
      .cs-summary { position: static; }
    }
    @media (max-width: 760px) {
      .cs-tabs { grid-template-columns: repeat(4, auto); justify-content: space-between; overflow-x: auto; padding: 0 .7rem; }
      .cs-tabs button:not(.current) .cs-tab-text { display: none; }
      .cs-tab-text small { display: none; }
      .cs-head .cs-btn span { display: none; }
    }
    @media (max-width: 640px) {
      .cs-overlay { padding: 0; }
      .cs-modal { height: 100dvh; border-radius: 0; }
      .cs-grid-2, .cs-grid-4, .cs-choice, .cs-facts { grid-template-columns: 1fr; }
      .cs-grid-2 .cs-field { margin-bottom: .85rem; }
      .cs-tier-form { grid-template-columns: 1fr 1fr 1fr auto; }
      .cs-foot { flex-wrap: wrap; padding: .65rem .8rem; }
      .cs-foot .cs-btn.ghost span { display: none; }
      .cs-foot-msg { order: -1; flex-basis: 100%; }
      .cs-head { padding: .7rem .8rem; }
      .cs-head-icon { display: none; }
    }
  `]
})
export class CrearSalaComponent implements OnInit, OnDestroy {
  /** true: abre directamente la lista de borradores (botón «Borradores» del dashboard). */
  @Input() openDraftsOnStart = false;
  /** Se emite al cerrar (creado o cancelado). Sin oyente (ruta /crear-sala), vuelve a «Mis espacios». */
  @Output() closed = new EventEmitter<{ createdSpaceId?: number }>();

  readonly categories = [
    { value: 'Coworking', label: 'Espacio de coworking' },
    { value: 'Sala de reuniones', label: 'Sala de reuniones' },
    { value: 'Oficina privada', label: 'Oficina privada' },
    { value: 'Sala de capacitación', label: 'Sala para capacitaciones' },
    { value: 'Sala de entrevistas', label: 'Sala para entrevistas o consultas' },
    { value: 'Sala de juntas', label: 'Sala de juntas directivas' },
    { value: 'Eventos', label: 'Salón para eventos y celebraciones' },
    { value: 'Conferencias', label: 'Auditorio o sala de conferencias' },
    { value: 'Estudio creativo', label: 'Taller o estudio creativo' },
    { value: 'Podcast', label: 'Estudio para podcast y grabación de audio' },
    { value: 'Fotografía', label: 'Estudio fotográfico' },
    { value: 'Video', label: 'Estudio de video y producción audiovisual' },
    { value: 'Consultorio privado', label: 'Consultorio privado' },
    { value: 'Aula o salón de clases', label: 'Aula o salón de clases' },
    { value: 'Taller práctico', label: 'Espacio para talleres prácticos' },
    { value: 'Estudio de música y ensayo', label: 'Estudio de música y sala de ensayo' },
    { value: 'Cocina para eventos o producción', label: 'Cocina para eventos o producción' },
    { value: 'Bienestar y yoga', label: 'Espacio para bienestar, yoga o terapias' },
    { value: 'Terraza o espacio al aire libre', label: 'Terraza o espacio al aire libre' },
    { value: 'Sala de exhibición', label: 'Sala de exhibición o showroom' },
    { value: 'Videojuegos y esports', label: 'Sala de videojuegos y esports' },
    { value: 'Cabina para videollamadas', label: 'Cabina privada para videollamadas' },
    { value: 'Espacio infantil', label: 'Espacio para actividades infantiles' },
    { value: 'Otro', label: 'Otro tipo de espacio' },
  ];
  readonly departments: string[] = [...EL_SALVADOR_DEPARTMENTS];
  readonly steps = STEPS;
  readonly dayNames = DAYS;
  readonly descriptionMin = DESCRIPTION_MIN;
  readonly capacityMax = CAPACITY_MAX;
  readonly priceMax = PRICE_MAX;
  readonly imageAccept = IMAGE_UPLOAD_ACCEPT;

  crearSalaForm!: FormGroup;
  tierDraftForm!: FormGroup;
  private formChanges?: Subscription;

  readonly currentStep = signal<StepId>('info');
  readonly stepIndex = computed(() => STEPS.findIndex(step => step.id === this.currentStep()));
  private readonly attempted = signal<Set<StepId>>(new Set());
  readonly stepMessage = signal('');
  /** Se incrementa al cambiar valores/validar para refrescar estados y errores. */
  private readonly validationTick = signal(0);

  readonly isCreating = signal(false);
  readonly uploadingImages = signal(false);
  readonly commissionRate = signal<number>(5);
  readonly stagedTiers = signal<CreatePricingTierRequest[]>([]);
  readonly stagedExtras = signal<StagedExtra[]>([]);
  readonly stagedFeatures = signal<SpaceFeatureInput[]>([]);
  readonly tierError = signal('');
  readonly useCustomSchedule = signal(false);
  readonly schedule = signal<ScheduleDay[]>(defaultSchedule());
  readonly scheduleAttempted = signal(false);
  private scheduleGen = 0;
  /** Fotos del borrador (ya en el bucket) y las que se están subiendo. */
  readonly selectedImages = signal<PhotoItem[]>([]);
  readonly removingPhoto = signal<string | null>(null);

  // Borradores (servidor)
  readonly draftsOpen = signal(false);
  readonly draftCount = signal(0);
  readonly loadedDraft = signal<LoadedDraft | null>(null);
  readonly savingDraft = signal(false);
  readonly conflict = signal<string | null>(null);
  readonly exitPromptOpen = signal(false);
  readonly editorsGen = signal(0);
  draftFeatures: SpaceFeatureInput[] | null = null;
  draftExtras: StagedExtra[] | null = null;
  private savedSnapshot = '';
  private closeAfterDraftsModal = false;
  /** Los editores emiten su lista al restaurarse: eso no cuenta como cambio. */
  private restoringEditors = false;

  readonly limitedExtras = computed(() => this.stagedExtras().filter(extra => extra.max_quantity).length);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('crearForm') crearFormRef!: ElementRef<HTMLFormElement>;

  private previousBodyOverflow = '';

  constructor(
    private router: Router,
    private salaService: SalaService,
    private authService: AuthService,
    private subscriptionService: SubscriptionService,
    public spaceScheduleService: SpaceScheduleService,
    private fb: FormBuilder,
    private dialogs: DialogService,
    private platformSettings: PlatformSettingsService,
    private notifications: NotificationService,
    private draftsService: SpaceDraftsService,
  ) {}

  get businessId(): number | null { return this.authService.currentUser()?.id ?? null; }

  ngOnInit(): void {
    this.initForm();
    this.platformSettings.get().subscribe({
      next: (r) => { this.commissionRate.set(r.settings.commission_rate); },
      error: () => {},
    });
    this.checkSpaceLimit();
    this.refreshDraftCount();
    // Limpieza de la versión anterior (borrador solo en este navegador).
    try { Object.keys(localStorage).filter(key => key.startsWith('tusala_space_draft_v1_')).forEach(key => localStorage.removeItem(key)); } catch { /* sin almacenamiento */ }
    this.savedSnapshot = this.snapshot();
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (this.openDraftsOnStart) { this.closeAfterDraftsModal = true; this.draftsOpen.set(true); }
  }

  ngOnDestroy(): void {
    this.formChanges?.unsubscribe();
    this.selectedImages().filter(image => image.uploading).forEach(image => URL.revokeObjectURL(image.url));
    document.body.style.overflow = this.previousBodyOverflow;
  }

  initForm(): void {
    const trimmedMin = (min: number) => (control: AbstractControl) => {
      const length = String(control.value ?? '').trim().length;
      return length > 0 && length < min ? { minTrimmed: { min } } : null;
    };
    const integer = (control: AbstractControl) =>
      control.value === null || control.value === '' || Number.isInteger(Number(control.value)) ? null : { integer: true };
    const twoDecimals = (control: AbstractControl) =>
      control.value === null || control.value === '' || /^\d+(\.\d{1,2})?$/.test(String(control.value)) ? null : { decimals: true };
    const required = (control: AbstractControl) =>
      typeof control.value === 'string' && !control.value.trim() ? { required: true } : Validators.required(control);

    this.crearSalaForm = this.fb.group({
      title: ['', [required, trimmedMin(TITLE_MIN), Validators.maxLength(100)]],
      description: ['', [required, trimmedMin(DESCRIPTION_MIN), Validators.maxLength(2000)]],
      categories: [['Coworking'], [Validators.required, Validators.maxLength(8)]],
      // Sin valor inicial (antes 0): así no aparece "Mínimo 1 persona" al abrir.
      capacity: [null, [Validators.required, Validators.min(1), Validators.max(CAPACITY_MAX), integer]],
      price_per_hour: [null, [Validators.required, Validators.min(0), Validators.max(PRICE_MAX), twoDecimals]],
      address_line1: ['', [required, Validators.maxLength(255)]],
      city: ['', required],
      department: ['', required],
      country: ['El Salvador', required],
      latitude: [null, [Validators.required, Validators.min(-90), Validators.max(90)]],
      longitude: [null, [Validators.required, Validators.min(-180), Validators.max(180)]],
      google_place_id: [null]
    });
    this.formChanges?.unsubscribe();
    this.formChanges = this.crearSalaForm.valueChanges.subscribe(() => this.validationTick.update(n => n + 1));

    this.tierDraftForm = this.fb.group({
      min_hours: [null, [Validators.required, Validators.min(1), halfHourIncrementValidator]],
      max_hours: [null, [Validators.required, Validators.min(1), halfHourIncrementValidator]],
      total_price: [null, [Validators.required, Validators.min(0)]],
    });
  }

  /** Verifica el límite de espacios (según prueba/plan/personalización) antes de dejar completar el formulario. */
  private checkSpaceLimit(): void {
    if (this.authService.currentUser()?.subscription_exempt) return;
    forkJoin({
      subscriptionResponse: this.subscriptionService.getSubscription(),
      spacesResponse: this.salaService.getEspacios(1, ''),
    }).subscribe({
      next: ({ subscriptionResponse, spacesResponse }) => {
        const subscription = subscriptionResponse.subscription as any;
        const isTrial = subscription?.status === 'trial';
        const custom = Number(subscription?.custom_max_spaces);
        const planLimit = Number(subscription?.planDefinition?.limits?.max_spaces);
        const limit = Number.isInteger(custom) && custom > 0 ? custom : (Number.isFinite(planLimit) ? planLimit : 0);
        const reached = limit > 0 && (isTrial || limit < 9999) && spacesResponse.total >= limit;
        if (!reached) return;
        const limitLabel = limit >= 9999 ? 'ilimitados' : `${limit} espacios`;
        void this.dialogs.open({
          tone: 'warning',
          title: 'Alcanzaste el límite de espacios',
          message: isTrial
            ? `Tu cuenta de prueba permite ${limitLabel}. Contrata un plan para ampliar el límite.`
            : `Tu suscripción permite ${limitLabel}. Para publicar más, mejora tu plan o solicita a Administración una capacidad personalizada.`,
          confirmText: 'Ver mi suscripción',
          cancelText: 'Volver a mis espacios',
        }).then(result => {
          this.finish();
          void this.router.navigate(['/dashboard'], { queryParams: { module: result ? 'suscripcion' : 'salas' } });
        });
      },
      error: () => {},
    });
  }

  // ---------------- Secciones ----------------
  private stepDef(id: StepId): StepDef { return STEPS.find(step => step.id === id)!; }

  stepValid(id: StepId): boolean {
    this.validationTick();
    if (id === 'horarios') return this.scheduleErrors().length === 0;
    return this.stepDef(id).fields.every(field => this.crearSalaForm.get(field)?.valid);
  }

  /**
   * Estado según los DATOS (no según si se visitó): completa = válida;
   * error = se intentó avanzar y falta algo; si no, pendiente. Horarios solo
   * se marca completa si se definió un horario específico válido.
   */
  stepState(id: StepId): 'done' | 'error' | 'todo' {
    const valid = this.stepValid(id);
    if (!valid && this.attempted().has(id)) return 'error';
    if (id === 'horarios') return this.useCustomSchedule() && valid ? 'done' : 'todo';
    return valid ? 'done' : 'todo';
  }

  stepCaption(step: StepDef): string {
    const state = this.stepState(step.id);
    if (state === 'error') return 'Revisa los datos marcados';
    if (step.id === 'horarios') return this.useCustomSchedule() ? 'Horario específico' : 'Sin restricción · revisa y crea';
    if (state === 'done') {
      if (step.id === 'info') return `Completa${this.selectedImages().length ? ` · ${this.selectedImages().length} foto(s)` : ''}`;
      if (step.id === 'precio') return `Completa${this.stagedExtras().length ? ` · ${this.stagedExtras().length} extra(s)` : ''}`;
      return 'Completa';
    }
    const missing = this.stepDef(step.id).fields.filter(field => !this.crearSalaForm.get(field)?.valid).length;
    return `Falta${missing === 1 ? '' : 'n'} ${missing} dato${missing === 1 ? '' : 's'}`;
  }

  /** Marca la sección como intentada; si no es válida, enfoca el primer campo con error. */
  private validateStep(id: StepId): boolean {
    this.attempted.update(set => new Set(set).add(id));
    if (id === 'horarios') this.scheduleAttempted.set(true);
    this.stepDef(id).fields.forEach(field => {
      this.crearSalaForm.get(field)?.markAsTouched();
      if (field === 'latitude') this.crearSalaForm.get('longitude')?.markAsTouched();
    });
    this.validationTick.update(n => n + 1);
    const valid = this.stepValid(id);
    this.stepMessage.set(valid ? '' : 'Completa los campos marcados para continuar.');
    if (!valid) this.focusFirstError(id);
    return valid;
  }

  private focusFirstError(id: StepId): void {
    const field = this.stepDef(id).fields.find(name => this.crearSalaForm.get(name)?.invalid);
    setTimeout(() => {
      const root = this.crearFormRef?.nativeElement;
      const holder = field ? root?.querySelector<HTMLElement>(`[data-field="${field}"]`) : root?.querySelector<HTMLElement>('.cs-day.invalid, .cs-panel:not(.hidden) .cs-err');
      holder?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      holder?.querySelector<HTMLElement>('input, textarea, button')?.focus({ preventScroll: true });
    }, 30);
  }

  private show(id: StepId): void {
    this.currentStep.set(id);
    this.stepMessage.set('');
    this.crearFormRef?.nativeElement?.scrollTo({ top: 0 });
  }

  next(): void {
    const id = this.currentStep();
    if (!this.validateStep(id)) return;
    const index = this.stepIndex();
    if (index < STEPS.length - 1) this.show(STEPS[index + 1].id);
  }

  back(): void {
    const index = this.stepIndex();
    if (index > 0) this.show(STEPS[index - 1].id);
  }

  /** Las secciones se abren en cualquier orden; validar solo ocurre al avanzar o crear. */
  goTo(id: StepId): void { this.show(id); }

  onSubmit(): void {
    if (this.currentStep() === 'horarios') void this.createEspacio();
    else this.next();
  }

  // ---------------- Errores por campo ----------------
  touch(field: string): void {
    this.crearSalaForm.get(field)?.markAsTouched();
    this.validationTick.update(n => n + 1);
  }

  /** Mensaje de error de un campo, solo si ya se tocó o se intentó avanzar desde su sección. */
  err(field: string): string | null {
    this.validationTick();
    const control = this.crearSalaForm.get(field);
    if (!control || control.valid) return null;
    const step = STEPS.find(item => item.fields.includes(field));
    const attemptedStep = !!(step && this.attempted().has(step.id));
    if (!attemptedStep && !control.touched) return null;
    // Entrar y salir de un campo vacío no es un error: solo tras escribir o al avanzar.
    if (!attemptedStep && !control.dirty && control.hasError('required')) return null;
    const e = control.errors || {};
    switch (field) {
      case 'title':
        if (e['required']) return 'Escribe el nombre del espacio.';
        if (e['minTrimmed']) return `Usa al menos ${TITLE_MIN} caracteres.`;
        return 'Máximo 100 caracteres.';
      case 'description':
        if (e['required']) return 'Describe el espacio para que el cliente sepa qué reserva.';
        if (e['minTrimmed']) return `Agrega un poco más de detalle (mínimo ${DESCRIPTION_MIN} caracteres).`;
        return 'Máximo 2000 caracteres.';
      case 'categories': return 'Elige al menos una categoría.';
      case 'capacity':
        if (e['required']) return 'Indica cuántas personas caben.';
        if (e['integer']) return 'Usa un número entero de personas.';
        if (e['max']) return `La capacidad máxima es ${CAPACITY_MAX}.`;
        return 'Debe ser al menos 1 persona.';
      case 'price_per_hour':
        if (e['required']) return 'Indica el precio por hora.';
        if (e['decimals']) return 'Usa como máximo 2 decimales.';
        if (e['max']) return `El precio máximo es $${PRICE_MAX}.`;
        return 'El precio no puede ser negativo.';
      case 'country': return 'Indica el país.';
      case 'department': return 'Elige el departamento.';
      case 'city': return 'Indica la ciudad.';
      case 'address_line1': return e['maxlength'] ? 'Máximo 255 caracteres.' : 'Escribe la dirección.';
      case 'latitude': return 'Coloca el pin en el mapa (busca la dirección o usa «Usar mi ubicación»).';
      default: return 'Revisa este dato.';
    }
  }

  // ---------------- Fotos ----------------
  triggerFileInput(): void { this.fileInput?.nativeElement?.click(); }

  private photoSeq = 0;

  /**
   * Las fotos se suben directo al bucket y quedan en el borrador (si aún no
   * hay borrador, se crea uno). Así nunca se pierden por cerrar la ventana ni
   * se suben dos veces al crear el espacio.
   */
  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const files = Array.from(input.files);
    input.value = '';
    const validationError = validateSpaceImages(files);
    if (validationError) { this.notifications.error(validationError); return; }
    if (!this.loadedDraft()) {
      const created = await this.saveDraft({ silent: true });
      if (!created) return;
      this.notifications.success('Guardamos un borrador para conservar tus fotos.');
    }
    const draftId = this.loadedDraft()!.id;
    const pending: PhotoItem[] = files.map(file => ({ key: `up-${++this.photoSeq}`, url: URL.createObjectURL(file), uploading: true }));
    this.selectedImages.update(list => [...list, ...pending]);
    this.uploadingImages.set(true);
    try {
      const saved = await firstValueFrom(this.draftsService.uploadImages(draftId, files));
      const pendingKeys = new Set(pending.map(item => item.key));
      this.selectedImages.update(list => [...list.filter(item => !pendingKeys.has(item.key)), ...saved.map(image => this.toPhoto(image))]);
    } catch (error) {
      const pendingKeys = new Set(pending.map(item => item.key));
      this.selectedImages.update(list => list.filter(item => !pendingKeys.has(item.key)));
      this.notifications.error((error as SpaceDraftError)?.message || 'No se pudieron subir las fotos.');
    } finally {
      pending.forEach(item => URL.revokeObjectURL(item.url));
      this.uploadingImages.set(false);
    }
  }

  private toPhoto(image: SpaceDraftImage): PhotoItem {
    return { key: `img-${image.image_id}`, image_id: image.image_id, url: image.url, uploading: false };
  }

  /** Quitar una foto la borra del bucket por completo (con confirmación). */
  async removeSelectedImage(photo: PhotoItem): Promise<void> {
    const draft = this.loadedDraft();
    if (!draft || !photo.image_id) return;
    const ok = await this.dialogs.confirm({
      tone: 'danger',
      title: 'Eliminar foto',
      message: 'La foto se eliminará definitivamente del borrador y del almacenamiento.',
      confirmText: 'Eliminar foto',
    });
    if (!ok) return;
    this.removingPhoto.set(photo.key);
    try {
      await firstValueFrom(this.draftsService.removeImage(draft.id, photo.image_id));
      this.selectedImages.update(list => list.filter(item => item.key !== photo.key));
    } catch (error) {
      const e = error as SpaceDraftError;
      if (e?.code === 'IMAGE_NOT_FOUND') this.selectedImages.update(list => list.filter(item => item.key !== photo.key));
      else this.notifications.error(e?.message || 'No se pudo eliminar la foto.');
    } finally {
      this.removingPhoto.set(null);
    }
  }

  trackPhoto(_index: number, photo: PhotoItem): string { return photo.key; }

  // ---------------- Editores (características y extras) ----------------
  onFeaturesChange(list: SpaceFeatureInput[]): void { this.stagedFeatures.set(list); this.afterEditorRestore(); }
  onExtrasChange(list: StagedExtra[]): void { this.stagedExtras.set(list); this.afterEditorRestore(); }

  /** Si la emisión viene de restaurar un borrador, no cuenta como cambio sin guardar. */
  private afterEditorRestore(): void {
    if (this.restoringEditors) this.savedSnapshot = this.snapshot();
  }

  // ---------------- Precio ----------------
  netPricePerHour(): number {
    this.validationTick();
    const price = Number(this.crearSalaForm?.get('price_per_hour')?.value) || 0;
    if (price <= 0) return 0;
    return price * (1 - this.commissionRate() / 100);
  }

  addStagedTier(): void {
    this.tierError.set('');
    const { min_hours, max_hours, total_price } = this.tierDraftForm.value;
    if (this.tierDraftForm.invalid) {
      this.tierDraftForm.markAllAsTouched();
      const halfHour = this.tierDraftForm.get('min_hours')?.hasError('halfHourIncrement') || this.tierDraftForm.get('max_hours')?.hasError('halfHourIncrement');
      this.tierError.set(halfHour ? 'Las horas van en intervalos de media hora (por ejemplo 2 o 2.5).' : 'Completa desde, hasta y el precio total (las horas desde 1).');
      return;
    }
    if (Number(min_hours) > Number(max_hours)) { this.tierError.set('«Desde» no puede ser mayor que «Hasta».'); return; }
    const overlap = this.stagedTiers().some(t => Number(min_hours) <= t.max_hours && Number(max_hours) >= t.min_hours);
    if (overlap) { this.tierError.set('Ese rango de horas se cruza con uno que ya agregaste.'); return; }
    this.stagedTiers.update(list => [...list, {
      min_hours: Number(min_hours), max_hours: Number(max_hours), total_price: Number(total_price),
    }].sort((a, b) => a.min_hours - b.min_hours));
    this.tierDraftForm.reset();
  }

  removeStagedTier(index: number): void {
    this.stagedTiers.update(list => list.filter((_, i) => i !== index));
  }

  // ---------------- Horarios ----------------
  setCustomSchedule(custom: boolean): void {
    this.useCustomSchedule.set(custom);
    if (!custom) this.scheduleAttempted.set(false);
  }

  setDayOpen(index: number, open: boolean): void {
    this.schedule.update(days => days.map((day, i) => i === index ? { ...day, closed: !open } : day));
  }

  setDayTime(index: number, key: 'opening' | 'closing', value: string): void {
    this.schedule.update(days => days.map((day, i) => i === index ? { ...day, [key]: value } : day));
  }

  trackDay = (index: number) => `${this.scheduleGen}-${index}`;

  applyPreset(preset: 'weekdays' | 'all'): void {
    this.scheduleGen++;
    this.schedule.set(DAYS.map((_, i) => ({ closed: preset === 'weekdays' && (i === 0 || i === 6), opening: '08:00', closing: '17:00' })));
  }

  copyFirstOpenDay(): void {
    const first = this.schedule().find(day => !day.closed);
    if (!first) { this.notifications.error('Abre al menos un día para copiar su horario.'); return; }
    this.scheduleGen++;
    this.schedule.update(days => days.map(day => day.closed ? day : { ...day, opening: first.opening, closing: first.closing }));
  }

  dayError(index: number): string | null {
    const day = this.schedule()[index];
    if (!this.useCustomSchedule() || day.closed) return null;
    if (!day.opening || !day.closing) return 'Indica la hora de apertura y de cierre.';
    const [, om] = day.opening.split(':').map(Number);
    const [, cm] = day.closing.split(':').map(Number);
    if ((om !== 0 && om !== 30) || (cm !== 0 && cm !== 30)) return 'Usa horas en punto o y media.';
    if (day.closing <= day.opening) return 'El cierre debe ser después de la apertura.';
    return null;
  }

  allClosed(): boolean { return this.useCustomSchedule() && this.schedule().every(day => day.closed); }

  private scheduleErrors(): string[] {
    if (!this.useCustomSchedule()) return [];
    const errors = this.schedule().map((_, i) => this.dayError(i)).filter((e): e is string => !!e);
    if (this.allClosed()) errors.push('all-closed');
    return errors;
  }

  scheduleSummary(): string {
    if (!this.useCustomSchedule()) return 'Sin restricción';
    const open = this.schedule().map((day, i) => ({ ...day, name: DAYS[i] })).filter(day => !day.closed);
    if (!open.length) return 'Todos los días cerrados';
    const sameHours = open.every(day => day.opening === open[0].opening && day.closing === open[0].closing);
    return sameHours
      ? `${open.map(day => day.name.slice(0, 2)).join(', ')} · ${formatClockTime(open[0].opening)}–${formatClockTime(open[0].closing)}`
      : `${open.length} días con horarios distintos`;
  }

  private buildScheduleRequest(): CreateScheduleRequest[] {
    return this.schedule().map((day, index) => ({
      day_of_week: index,
      opening_time: day.closed ? '00:00' : day.opening,
      closing_time: day.closed ? '00:00' : day.closing,
      is_closed: day.closed,
    }));
  }

  reviewWarnings(): string[] {
    this.validationTick();
    const warnings: string[] = [];
    if (!this.selectedImages().length) warnings.push('Sin fotos: los espacios con fotos reciben más reservas.');
    if (!this.stagedFeatures().length) warnings.push('No indicaste qué incluye el espacio (Wi-Fi, pantalla…).');
    const price = this.crearSalaForm.get('price_per_hour')?.value;
    if (price !== null && price !== '' && Number(price) === 0) warnings.push('El precio por hora es $0: las reservas no tendrán costo.');
    return warnings;
  }

  // ---------------- Ubicación y categorías ----------------
  setCategories(values: string[]): void {
    const control = this.crearSalaForm.get('categories');
    control?.setValue(values);
    control?.markAsTouched();
    control?.markAsDirty();
  }

  setCity(value: string): void {
    const control = this.crearSalaForm.get('city');
    control?.setValue(value);
    control?.markAsTouched();
    control?.markAsDirty();
  }

  setDepartment(value: string): void {
    const control = this.crearSalaForm.get('department');
    control?.setValue(value);
    control?.markAsTouched();
    control?.markAsDirty();
    const cityControl = this.crearSalaForm.get('city');
    const currentCity = cityControl?.value;
    if (currentCity && !citiesForDepartment(value).includes(currentCity)) cityControl?.setValue('');
  }

  citiesForSelectedDepartment(): string[] {
    return citiesForDepartment(this.crearSalaForm?.get('department')?.value);
  }

  get fullAddress(): string {
    if (!this.crearSalaForm) return '';
    const value = this.crearSalaForm.value;
    return [value.address_line1, value.city, value.department, value.country].filter(Boolean).join(', ');
  }

  setMapLocation(location: SpaceMapLocation): void {
    this.crearSalaForm.patchValue({ latitude: location.latitude, longitude: location.longitude, google_place_id: location.google_place_id || null });
    this.crearSalaForm.get('latitude')?.markAsTouched();
    this.crearSalaForm.get('longitude')?.markAsTouched();
  }

  // ---------------- Borradores (servidor) ----------------
  private draftData(): SpaceDraftData {
    return {
      form: this.crearSalaForm.getRawValue(),
      tiers: this.stagedTiers(),
      extras: this.stagedExtras(),
      features: this.stagedFeatures(),
      useCustomSchedule: this.useCustomSchedule(),
      schedule: this.schedule(),
    };
  }

  /** Estado serializable (sin fotos) para detectar cambios sin guardar. */
  private snapshot(): string {
    const { google_place_id, ...form } = this.crearSalaForm.getRawValue();
    return JSON.stringify({ form, tiers: this.stagedTiers(), extras: this.stagedExtras(), features: this.stagedFeatures(), custom: this.useCustomSchedule(), schedule: this.useCustomSchedule() ? this.schedule() : null });
  }

  hasUnsavedChanges(): boolean { this.validationTick(); return this.snapshot() !== this.savedSnapshot; }

  private draftProgress(): number {
    const required: StepId[] = ['info', 'precio', 'ubicacion'];
    return Math.round((required.filter(step => this.stepValid(step)).length / required.length) * 100);
  }

  private refreshDraftCount(): void {
    const businessId = this.businessId;
    if (!businessId) return;
    this.draftsService.list(businessId).subscribe({ next: r => this.draftCount.set(r.drafts.length), error: () => {} });
  }

  /** Guarda (crea o actualiza) el borrador en el servidor. Devuelve true si quedó guardado. */
  async saveDraft(options: { silent?: boolean; asNew?: boolean } = {}): Promise<boolean> {
    const businessId = this.businessId;
    if (!businessId) { this.notifications.error('No se pudo identificar el negocio para guardar el borrador.'); return false; }
    const payload = {
      title: String(this.crearSalaForm.get('title')?.value || '').trim(),
      current_step: this.currentStep(),
      progress: this.draftProgress(),
      data: this.draftData(),
    };
    const loaded = options.asNew ? null : this.loadedDraft();
    this.savingDraft.set(true);
    try {
      const saved = loaded
        ? await firstValueFrom(this.draftsService.update(loaded.id, { ...payload, version: loaded.version }))
        : await firstValueFrom(this.draftsService.create(businessId, payload));
      this.loadedDraft.set({ id: saved.draft_id, version: saved.version, updatedBy: saved.updated_by_name, updatedAt: saved.updated_at });
      this.savedSnapshot = this.snapshot();
      this.validationTick.update(n => n + 1);
      if (!loaded) this.draftCount.update(n => n + 1);
      if (!options.silent) this.notifications.success('Borrador guardado. Tu equipo puede continuarlo desde «Borradores».');
      return true;
    } catch (error) {
      const e = error as SpaceDraftError;
      if (e?.code === 'DRAFT_VERSION_CONFLICT') {
        this.conflict.set(e.message);
      } else if (e?.code === 'DRAFT_NOT_FOUND' && loaded) {
        // Lo eliminaron o se usó: se guarda como uno nuevo.
        this.loadedDraft.set(null);
        this.draftCount.update(n => Math.max(0, n - 1));
        this.savingDraft.set(false);
        return this.saveDraft(options);
      } else {
        this.notifications.error(e?.message || 'No se pudo guardar el borrador.');
      }
      return false;
    } finally {
      this.savingDraft.set(false);
    }
  }

  async saveAsNewDraft(): Promise<void> {
    this.conflict.set(null);
    if (await this.saveDraft({ asNew: true }) && this.selectedImages().length) {
      // Las fotos pertenecen al borrador original (siguen ahí).
      this.selectedImages.set([]);
      this.notifications.success('Guardado como borrador nuevo. Las fotos siguen en el borrador original.');
    }
  }

  async reloadLoadedDraft(): Promise<void> {
    const loaded = this.loadedDraft();
    this.conflict.set(null);
    if (loaded) await this.loadDraft(loaded.id);
  }

  openDrafts(): void {
    this.closeAfterDraftsModal = false;
    this.draftsOpen.set(true);
  }

  onDraftsClosed(): void {
    this.draftsOpen.set(false);
    // Abierto desde «Borradores» del dashboard y sin elegir nada: se cierra todo.
    if (this.closeAfterDraftsModal && !this.loadedDraft() && !this.hasUnsavedChanges()) this.finish();
    this.closeAfterDraftsModal = false;
  }

  async onDraftSelected(summary: SpaceDraftSummary): Promise<void> {
    if (this.loadedDraft()?.id === summary.draft_id && !this.hasUnsavedChanges()) { this.draftsOpen.set(false); return; }
    if (this.hasUnsavedChanges()) {
      const ok = await this.dialogs.confirm({
        tone: 'warning',
        title: 'Reemplazar lo que tienes',
        message: `Se cargará «${summary.title || 'Espacio sin nombre'}» y perderás los cambios sin guardar de esta pantalla.`,
        confirmText: 'Cargar borrador',
      });
      if (!ok) return;
    }
    this.draftsOpen.set(false);
    this.closeAfterDraftsModal = false;
    await this.loadDraft(summary.draft_id);
  }

  private async loadDraft(draftId: number): Promise<void> {
    try {
      const draft = await firstValueFrom(this.draftsService.get<SpaceDraftData>(draftId));
      this.applyDraftData(draft.data);
      this.selectedImages.set((draft.images || []).map(image => this.toPhoto(image)));
      this.loadedDraft.set({ id: draft.draft_id, version: draft.version, updatedBy: draft.updated_by_name, updatedAt: draft.updated_at });
      this.show((STEPS.some(step => step.id === draft.current_step) ? draft.current_step : 'info') as StepId);
      this.savedSnapshot = this.snapshot();
      this.validationTick.update(n => n + 1);
      // Las emisiones de los editores al restaurarse (siguiente ciclo) tampoco son cambios.
      this.restoringEditors = true;
      setTimeout(() => { this.restoringEditors = false; }, 1500);
      this.notifications.success(`Continuando «${draft.title || 'Espacio sin nombre'}».`);
    } catch (error) {
      const e = error as SpaceDraftError;
      this.notifications.error(e?.message || 'No se pudo abrir el borrador.');
      this.refreshDraftCount();
    }
  }

  private applyDraftData(data: Partial<SpaceDraftData> | null | undefined): void {
    const safe = data || {};
    this.initForm();
    this.crearSalaForm.patchValue(safe.form || {});
    this.stagedTiers.set(Array.isArray(safe.tiers) ? safe.tiers : []);
    this.stagedExtras.set(Array.isArray(safe.extras) ? safe.extras : []);
    this.stagedFeatures.set(Array.isArray(safe.features) ? safe.features : []);
    this.useCustomSchedule.set(!!safe.useCustomSchedule);
    this.scheduleGen++;
    this.schedule.set(Array.isArray(safe.schedule) && safe.schedule.length === 7 ? safe.schedule : defaultSchedule());
    // Los editores de características y extras se recrean con la lista del borrador.
    this.draftFeatures = this.stagedFeatures();
    this.draftExtras = this.stagedExtras();
    this.editorsGen.update(n => n + 1);
    this.attempted.set(new Set());
    this.scheduleAttempted.set(false);
    this.tierError.set('');
    this.stepMessage.set('');
  }

  trackGen(_index: number, gen: number): number { return gen; }

  // ---------------- Cerrar ----------------
  requestClose(): void {
    if (this.isCreating()) return;
    if (this.hasUnsavedChanges()) this.exitPromptOpen.set(true);
    else this.finish();
  }

  async saveDraftAndClose(): Promise<void> {
    if (await this.saveDraft({ silent: true })) {
      this.notifications.success('Borrador guardado. Lo encontrarás en «Borradores» al crear un espacio.');
      this.finish();
    } else {
      this.exitPromptOpen.set(false);
    }
  }

  closeNow(): void { this.finish(); }

  private finish(createdSpaceId?: number): void {
    this.exitPromptOpen.set(false);
    if (this.closed.observed) {
      this.closed.emit({ createdSpaceId });
    } else if (!createdSpaceId) {
      void this.router.navigate(['/dashboard'], { queryParams: { module: 'salas' } });
    }
  }

  onOverlayMouseDown(event: MouseEvent): void {
    // Clic fuera del modal = intento de cierre (con la misma protección).
    if (event.target === event.currentTarget) this.requestClose();
  }

  /** Escape: primero cierra lo que esté encima (modales internos, diálogos, avisos). */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (document.querySelector('.sf-overlay, .ex-overlay, .dialog-overlay, .dm-overlay, .flatpickr-calendar.open')) return;
    if (this.conflict()) { this.conflict.set(null); return; }
    if (this.exitPromptOpen()) { this.exitPromptOpen.set(false); return; }
    this.requestClose();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges() && !this.isCreating()) event.preventDefault();
  }

  // ---------------- Crear ----------------
  /** Aplica al espacio recién creado las tarifas, extras y horario armados antes. */
  private async applyStagedDetails(spaceId: number): Promise<{ tiersFailed: number; extrasFailed: number; scheduleFailed: boolean; featuresFailed: boolean }> {
    let tiersFailed = 0;
    let extrasFailed = 0;
    let scheduleFailed = false;
    let featuresFailed = false;

    if (this.stagedFeatures().length) {
      try { await firstValueFrom(this.salaService.saveSpaceFeatures(spaceId, this.stagedFeatures())); }
      catch { featuresFailed = true; }
    }
    for (const tier of this.stagedTiers()) {
      try { await firstValueFrom(this.salaService.crearPricingTier(spaceId, tier)); }
      catch { tiersFailed++; }
    }
    for (const extra of this.stagedExtras()) {
      try {
        await firstValueFrom(this.salaService.crearExtra(spaceId, {
          name: extra.name, description: extra.description || undefined, price: extra.price,
          is_available: extra.is_available, icon: extra.icon, max_quantity: extra.max_quantity,
        }));
      } catch { extrasFailed++; }
    }
    if (this.useCustomSchedule()) {
      try { await firstValueFrom(this.spaceScheduleService.setSchedules(spaceId, { schedules: this.buildScheduleRequest() })); }
      catch { scheduleFailed = true; }
    }
    return { tiersFailed, extrasFailed, scheduleFailed, featuresFailed };
  }

  async createEspacio(): Promise<void> {
    if (this.isCreating()) return;
    if (this.uploadingImages()) { this.notifications.error('Espera a que terminen de subirse las fotos.'); return; }
    for (const step of STEPS) {
      if (!this.stepValid(step.id)) {
        this.show(step.id);
        this.validateStep(step.id);
        return;
      }
    }
    const currentUser = this.authService.currentUser();
    if (!currentUser || !currentUser.id) {
      this.notifications.error('No se pudo obtener la información del negocio.');
      return;
    }

    const formValue = this.crearSalaForm.value;
    const espacioRequest: CreateEspacioRequest = {
      business_id: currentUser.id,
      title: String(formValue.title).trim(),
      description: String(formValue.description).trim(),
      category: formValue.categories[0],
      categories: formValue.categories,
      capacity: Number(formValue.capacity),
      price_per_hour: Number(formValue.price_per_hour),
      address_line1: String(formValue.address_line1).trim(),
      city: formValue.city,
      department: formValue.department,
      country: String(formValue.country).trim(),
      latitude: formValue.latitude,
      longitude: formValue.longitude,
      google_place_id: formValue.google_place_id
    };

    this.isCreating.set(true);
    try {
      // La revisión ya es la confirmación: se crea sin otro «¿estás seguro?».
      const espacioCreado = await this.dialogs.run(
        'Creando el espacio…',
        () => this.salaService.crearEspacio(espacioRequest),
        'Espacio creado',
        (error: any) => error?.error?.code === 'SPACE_LIMIT_REACHED' ? 'Alcanzaste el límite de espacios de tu plan.' : friendlyError(error, 'No se pudo crear el espacio.'),
      );
      if (espacioCreado === null) return;
      const warnings: string[] = [];
      const { tiersFailed, extrasFailed, scheduleFailed, featuresFailed } = await this.applyStagedDetails(espacioCreado.space_id);
      if (tiersFailed) warnings.push(`${tiersFailed} precio(s) por cantidad de horas no se pudieron guardar`);
      if (extrasFailed) warnings.push(`${extrasFailed} extra(s) no se pudieron guardar`);
      if (scheduleFailed) warnings.push('el horario no se pudo guardar');
      if (featuresFailed) warnings.push('las características no se pudieron guardar');
      // Las fotos del borrador (ya en el bucket) pasan al espacio y el borrador se elimina.
      const loaded = this.loadedDraft();
      if (loaded) {
        try { await firstValueFrom(this.draftsService.adopt(loaded.id, espacioCreado.space_id)); }
        catch { warnings.push(this.selectedImages().length ? 'las fotos no se pudieron pasar al espacio (siguen en el borrador)' : 'el borrador no se pudo cerrar'); }
      }
      this.savedSnapshot = this.snapshot();
      this.salaService.loadEspacios();
      if (warnings.length) this.notifications.error(`Espacio creado. Revisa desde su detalle: ${warnings.join(', ')}.`);
      else this.notifications.success('Espacio creado exitosamente.');
      this.finish(espacioCreado.space_id);
      void this.router.navigate(['/sala', espacioCreado.space_id]);
    } catch (error: any) {
      if (error?.error?.code === 'SPACE_LIMIT_REACHED') {
        const limit = error.error?.limit?.max_spaces;
        const limitLabel = Number(limit) >= 9999 ? 'ilimitados' : `${limit} espacios`;
        void this.dialogs.open({ tone: 'warning', title: 'Alcanzaste el límite de espacios', message: `Tu suscripción permite ${limitLabel}. Para publicar más, mejora tu plan o solicita a Administración una capacidad personalizada.`, confirmText: 'Ver mi suscripción', cancelText: 'Volver a mis espacios' }).then(result => {
          if (result) { this.finish(); void this.router.navigate(['/dashboard'], { queryParams: { module: 'suscripcion' } }); }
        });
      }
    } finally {
      this.isCreating.set(false);
    }
  }
}
